import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
import { autoinject, BindingEngine } from 'aurelia-framework';
import * as L from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import { MapZonesController } from './map-zones-controller';
import { MapZoneDetailModel } from 'model/map-zones';
import { MapZoneLayerController } from 'components/locations/map-zone-layer-controller';
import { esriApiKey } from '../../../support/map';

@autoinject
export class MapZonesMap {
  private mapZonesMapPlaceholder: HTMLElement;
  private map: L.Map;
  private resizeObserver: ResizeObserver;
  private mapMinZoom = 2;
  private mapMaxZoom = 18;
  private mapZoneLayersControllers: MapZoneLayerController[] = [];

  constructor(private controller: MapZonesController, private bindingEngine: BindingEngine) {
  }

  attached() {
    this.controller.addMapZoneInMap = (mapZone: MapZoneDetailModel) => this.addMapZone(mapZone);
    this.controller.removeMapZoneInMap = (mapZone: MapZoneDetailModel) => this.removeMapZone(mapZone);
    this.controller.detailMapZoneInMap = (mapZone: MapZoneDetailModel) => this.detailMapZone(mapZone);
    this.controller.undetailMapZoneInMap = (mapZone: MapZoneDetailModel) => this.undetailMapZone(mapZone);
    this.controller.zoomMapZoneInMap = (mapZone: MapZoneDetailModel) => this.zoomMapZone(mapZone);
    this.createMap();
  }

  detached() {
    this.controller.mapCenter = this.map.getCenter();
    this.controller.mapZoom = this.map.getZoom();
    this.mapZoneLayersControllers.forEach(mapZone => {
      this.removeMapZone(mapZone.mapZone);
    })
    this.resizeObserver.disconnect();
  }

  private async createMap() {
    if (this.map == null) {
      L.PM.setOptIn(true)
      this.map = L.map(this.mapZonesMapPlaceholder, { pmIgnore: false } as L.MapOptions);
      this.setMapPreviousView(this.map);
      L.esri.Vector.vectorTileLayer(
        "https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer", 
        {
          apiKey: esriApiKey,
          token: null,
          portalUrl: null,
          style: null
        }
      ).addTo(this.map);
      this.map.setMaxZoom(this.mapMaxZoom);
      this.map.setMinZoom(this.mapMinZoom);
      this.map.pm.addControls({
        drawPolygon: true,
        drawMarker: false,
        drawCircleMarker: false,
        drawPolyline: false,
        drawRectangle: false,
        drawCircle: false,
        drawText: false,
        cutPolygon: false
      });
      if (this.map.pm.controlsVisible())
        this.map.pm.toggleControls();
    }
    const ResizeObserver = (<any>window).ResizeObserver || ResizeObserverPolyfill;
    this.resizeObserver = new ResizeObserver(() => { this.map.invalidateSize(); });
    this.resizeObserver.observe(this.mapZonesMapPlaceholder);
  }

  private addMapZone(mapZone: MapZoneDetailModel) {
    const mapZoneLayerController = new MapZoneLayerController(this.bindingEngine, mapZone)
    mapZoneLayerController.attach();
    this.mapZoneLayersControllers.push(mapZoneLayerController);
    mapZone.featureCollectionLayer
      .on('layeradd', (e) => {
        mapZone.data.featureCollection = JSON.stringify(mapZone.featureCollectionLayer.toGeoJSON());
        this.enableLayer(e.layer as L.GeoJSON);
        mapZoneLayerController.setMapZoneLayerStyle();
      })
      .on('layerremove', (e) => {
        mapZone.data.featureCollection = JSON.stringify(mapZone.featureCollectionLayer.toGeoJSON());
      })
      .on('pm:change', (e) => {
        mapZone.data.featureCollection = JSON.stringify(mapZone.featureCollectionLayer.toGeoJSON());
      })
      .on('click', (e) => {
        if (!mapZone.isDetailed && !this.map.pm.globalDrawModeEnabled())
          this.controller.detailMapZone(mapZone);
      })
      .on('mouseover', () => { mapZone.isHighlighted = true; })
      .on('mouseout', () => { mapZone.isHighlighted = false; });
    this.disableLayer(mapZone.featureCollectionLayer);
    mapZone.featureCollectionLayer.setStyle({ className: 'map-zone-layer' });
    mapZone.featureCollectionLayer.addTo(this.map);
  }

  private removeMapZone(mapZone: MapZoneDetailModel) {
    mapZone.featureCollectionLayer
      .off('layeradd')
      .off('layerremove')
      .off('pm:change')
      .off('click')
      .off('mouseover')
      .off('mouseout');
    mapZone.featureCollectionLayer.removeFrom(this.map);
    const removedMapZoneLayerControllers = this.mapZoneLayersControllers.splice(this.mapZoneLayersControllers.findIndex(x => x.mapZone === mapZone), 1);
    if (removedMapZoneLayerControllers.length === 1)
      removedMapZoneLayerControllers[0].unattach();
  }

  private detailMapZone(mapZone: MapZoneDetailModel) {
    this.map.pm.setGlobalOptions({ layerGroup: mapZone.featureCollectionLayer });
    if (mapZone.data.isActive) {
      this.enableLayer(mapZone.featureCollectionLayer);
      if (!this.map.pm.controlsVisible())
        this.map.pm.toggleControls();
    }
  }

  private undetailMapZone(mapZone: MapZoneDetailModel) {
    this.disableLayer(mapZone.featureCollectionLayer);
    if (this.map.pm.controlsVisible())
      this.map.pm.toggleControls();
    if (this.map.pm.globalDrawModeEnabled())
      this.map.pm.disableDraw();
    if (this.map.pm.globalEditModeEnabled())
      this.map.pm.disableGlobalEditMode();
    if (this.map.pm.globalDragModeEnabled())
      this.map.pm.disableGlobalDragMode();
    if (this.map.pm.globalRemovalModeEnabled())
      this.map.pm.disableGlobalRemovalMode();
    if (this.map.pm.globalRotateModeEnabled())
      this.map.pm.disableGlobalRotateMode();
  }

  private zoomMapZone(mapZone: MapZoneDetailModel) {
    if (mapZone.featureCollectionLayer.getLayers().length > 0)
      this.map.fitBounds(mapZone.featureCollectionLayer.getBounds(), { animate: false });
  }

  private setMapPreviousView(map: L.Map) {
    if (this.controller.mapZoom && this.controller.mapCenter)
      map.setView(this.controller.mapCenter, this.controller.mapZoom);
    else
      this.setMapDefaultView(map);
  }

  private enableLayer(layer: L.GeoJSON) {
    layer.setStyle({ pmIgnore: false });
    L.PM.reInitLayer(layer);
    layer.bringToFront();
  }

  private disableLayer(layer: L.GeoJSON) {
    layer.setStyle({ pmIgnore: true });
    layer.bringToBack();
  }

  private setMapDefaultView(map: L.Map) {
    map.setView([38.272689, 10.810547], 3);
  }
}
